/* Wonderly product page: reveal-on-scroll, animated Q&A, copy code, delivery estimate, carousel */
(() => {
  if (window.__wonderlyPdp) return;
  window.__wonderlyPdp = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('wpdp-js');

  /* ---------- Carousel (shared with the homepage shelves) ---------- */
  if (!customElements.get('wlm-carousel')) {
    class WlmCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.wlm-shelf__track');
        this.bar = this.querySelector('.wlm-shelf__progress span');
        if (!this.track) return;
        this.interval = parseInt(this.dataset.interval, 10) || 4500;
        this.autoplay = this.dataset.autoplay === 'true' && !reduced;
        this.paused = false; this.visible = false; this.holdUntil = 0;
        this.querySelectorAll('.wlm-arrow').forEach((btn) => {
          btn.addEventListener('click', () => { this.hold(); this.go(parseInt(btn.dataset.dir, 10)); });
        });
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.hold(); this.go(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.hold(); this.go(-1); }
        });
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        ['pointerdown', 'touchstart', 'wheel'].forEach((ev) => this.track.addEventListener(ev, () => this.hold(), { passive: true }));
        this.addEventListener('mouseenter', () => { this.paused = true; this.classList.add('is-paused'); });
        this.addEventListener('mouseleave', () => { this.paused = false; this.classList.remove('is-paused'); });
        this.addEventListener('focusin', () => { this.paused = true; });
        this.addEventListener('focusout', () => { this.paused = false; });
        this.onVis = () => { if (document.hidden) this.stop(); else this.start(); };
        document.addEventListener('visibilitychange', this.onVis);
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver((entries) => {
            this.visible = entries[0].isIntersecting;
            if (this.visible) this.start(); else this.stop();
          }, { threshold: 0.35 });
          this.io.observe(this);
        } else { this.visible = true; this.start(); }
        this.onResize = () => this.update();
        window.addEventListener('resize', this.onResize, { passive: true });
        this.update();
      }
      disconnectedCallback() {
        this.stop();
        if (this.io) this.io.disconnect();
        document.removeEventListener('visibilitychange', this.onVis);
        window.removeEventListener('resize', this.onResize);
      }
      step() {
        const card = this.track.firstElementChild;
        if (!card) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      }
      atEnd() { return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4; }
      atStart() { return this.track.scrollLeft <= 4; }
      go(dir) {
        const behavior = reduced ? 'auto' : 'smooth';
        if (dir > 0 && this.atEnd()) return this.track.scrollTo({ left: 0, behavior });
        if (dir < 0 && this.atStart()) return this.track.scrollTo({ left: this.track.scrollWidth, behavior });
        this.track.scrollBy({ left: dir * this.step(), behavior });
      }
      hold() { this.holdUntil = Date.now() + this.interval * 2; }
      start() {
        if (!this.autoplay || this.timer || !this.visible) return;
        this.timer = setInterval(() => {
          if (this.paused || Date.now() < this.holdUntil) return;
          this.go(1);
        }, this.interval);
      }
      stop() { clearInterval(this.timer); this.timer = null; }
      update() {
        if (!this.bar) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const visible = this.track.clientWidth / this.track.scrollWidth;
        const pos = max > 0 ? this.track.scrollLeft / max : 0;
        this.bar.style.width = (visible * 100).toFixed(2) + '%';
        this.bar.style.transform = 'translateX(' + (pos * (1 / visible - 1) * 100).toFixed(2) + '%)';
        this.classList.toggle('is-static', max <= 4);
      }
    }
    customElements.define('wlm-carousel', WlmCarousel);
  }

  /* ---------- Reveal on scroll ---------- */
  const io = !reduced && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
    : null;

  /* ---------- Delivery estimate ---------- */
  const addBusinessDays = (n) => {
    const d = new Date();
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  };
  const fmt = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  const init = (root = document) => {
    root.querySelectorAll('[data-wpdp-reveal]:not(.is-in):not([data-wpdp-seen])').forEach((el) => {
      el.setAttribute('data-wpdp-seen', '');
      if (io) io.observe(el); else el.classList.add('is-in');
    });
    root.querySelectorAll('[data-wpdp-eta][hidden]').forEach((el) => {
      const [a, b] = el.dataset.wpdpEta.split(',').map(Number);
      const out = el.querySelector('[data-eta-range]');
      if (!out || !a) return;
      out.textContent = a === b ? fmt(addBusinessDays(a)) : fmt(addBusinessDays(a)) + ' – ' + fmt(addBusinessDays(b));
      el.hidden = false;
    });
  };

  /* ---------- Q&A accordion + copy code ---------- */
  document.addEventListener('click', (e) => {
    const q = e.target.closest('.wpdp-qi__q');
    if (q) {
      const item = q.closest('.wpdp-qi');
      const open = q.getAttribute('aria-expanded') !== 'true';
      q.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-open', open);
      return;
    }
    const copy = e.target.closest('[data-wpdp-copy]');
    if (copy) {
      const label = copy.querySelector('[data-copy-label]');
      const done = () => {
        copy.classList.add('is-copied');
        if (label) label.textContent = 'Copied';
        setTimeout(() => { copy.classList.remove('is-copied'); if (label) label.textContent = 'Copy'; }, 1800);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(copy.dataset.wpdpCopy).then(done, done); else done();
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* The product form re-renders parts of the page when a variant changes */
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; init(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
